import { useEffect, useRef } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'

import { useAppState } from '../reduxish/appState'
import { isNativePlatform } from '../api/platform'

// Service-worker update/offline-ready UI. Only meaningful on the web/PWA build —
// native Capacitor apps ship bundled assets and never register a service worker,
// so this half is skipped there.
function ServiceWorkerUpdater() {
    const { state, dispatch } = useAppState()
    const updateSWRef = useRef<((reload?: boolean) => Promise<void>) | null>(null)

    const {
        needRefresh: [needRefresh, setNeedRefresh],
        offlineReady: [offlineReady, setOfflineReady],
        updateServiceWorker,
    } = useRegisterSW({
        onRegisteredSW(_swUrl, registration) {
            // Hourly poll for new SW. The browser revalidates registration.update().
            if (registration) {
                setInterval(() => {
                    registration.update().catch(() => { })
                }, 60 * 60 * 1000)
            }
        },
    })

    updateSWRef.current = updateServiceWorker

    useEffect(() => {
        dispatch({ type: 'PWA_UPDATE_AVAILABLE', value: needRefresh })
    }, [needRefresh, dispatch])

    useEffect(() => {
        dispatch({ type: 'PWA_OFFLINE_READY', value: offlineReady })
    }, [offlineReady, dispatch])

    const handleReload = () => {
        const fn = updateSWRef.current
        if (fn) fn(true)
    }

    return (
        <>
            <Snackbar
                open={state.pwa.updateAvailable}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity="info"
                    variant="filled"
                    action={
                        <>
                            <Button color="inherit" size="small" onClick={handleReload}>
                                Reload
                            </Button>
                            <Button color="inherit" size="small" onClick={() => setNeedRefresh(false)}>
                                Later
                            </Button>
                        </>
                    }
                >
                    A new version is available.
                </Alert>
            </Snackbar>

            <Snackbar
                open={state.pwa.offlineReady && !state.pwa.updateAvailable}
                autoHideDuration={4000}
                onClose={() => setOfflineReady(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" variant="filled" onClose={() => setOfflineReady(false)}>
                    Ready to use offline.
                </Alert>
            </Snackbar>
        </>
    )
}

export default function PwaUpdateBanner() {
    const { state, dispatch } = useAppState()

    // Online/offline awareness applies on every platform (webview included).
    useEffect(() => {
        const onOnline = () => dispatch({ type: 'PWA_ONLINE_STATUS_CHANGED', isOnline: true })
        const onOffline = () => dispatch({ type: 'PWA_ONLINE_STATUS_CHANGED', isOnline: false })
        window.addEventListener('online', onOnline)
        window.addEventListener('offline', onOffline)
        return () => {
            window.removeEventListener('online', onOnline)
            window.removeEventListener('offline', onOffline)
        }
    }, [dispatch])

    return (
        <>
            {!isNativePlatform() && <ServiceWorkerUpdater />}

            <Snackbar
                open={!state.pwa.isOnline}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="warning" variant="filled">
                    You are offline. Some features are unavailable.
                </Alert>
            </Snackbar>
        </>
    )
}
